import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Admin-only hook for reading and updating the `auth_settings` row.
 *
 * The row is a singleton (id = 1). RLS allows any signed-in user to read
 * it, but only super_admins can write. The UI enforces this too.
 */
export interface AdminAuthSettings {
  passwordHibpEnabled: boolean;
  anonymousSignInsEnabled: boolean;
  autoConfirmEmail: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export type AdminAuthSettingsStatus = "loading" | "ready" | "error";

export interface UseAdminAuthSettingsResult {
  status: AdminAuthSettingsStatus;
  settings: AdminAuthSettings | null;
  error: Error | null;
  saving: boolean;
  refetch: () => Promise<void>;
  save: (patch: Partial<Omit<AdminAuthSettings, "updatedAt" | "updatedBy">>) => Promise<void>;
}

function fromRow(row: {
  password_hibp_enabled: boolean;
  anonymous_sign_ins_enabled: boolean;
  auto_confirm_email: boolean;
  updated_at: string;
  updated_by: string | null;
}): AdminAuthSettings {
  return {
    passwordHibpEnabled: row.password_hibp_enabled,
    anonymousSignInsEnabled: row.anonymous_sign_ins_enabled,
    autoConfirmEmail: row.auto_confirm_email,
    updatedAt: row.updated_at,
    updatedBy: row.updated_by,
  };
}

export function useAdminAuthSettings(): UseAdminAuthSettingsResult {
  const [status, setStatus] = useState<AdminAuthSettingsStatus>("loading");
  const [settings, setSettings] = useState<AdminAuthSettings | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const refetch = useCallback(async () => {
    setStatus("loading");
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("auth_settings")
      .select("password_hibp_enabled, anonymous_sign_ins_enabled, auto_confirm_email, updated_at, updated_by")
      .eq("id", 1)
      .maybeSingle();

    if (fetchError) {
      setError(new Error(fetchError.message));
      setStatus("error");
      return;
    }
    if (!data) {
      setError(new Error("Auth settings row not found. Contact support."));
      setStatus("error");
      return;
    }
    setSettings(fromRow(data));
    setStatus("ready");
  }, []);

  const save = useCallback<UseAdminAuthSettingsResult["save"]>(
    async (patch) => {
      setSaving(true);
      setError(null);
      const row: {
        password_hibp_enabled?: boolean;
        anonymous_sign_ins_enabled?: boolean;
        auto_confirm_email?: boolean;
        updated_by?: string;
      } = {};
      if (patch.passwordHibpEnabled !== undefined) row.password_hibp_enabled = patch.passwordHibpEnabled;
      if (patch.anonymousSignInsEnabled !== undefined) row.anonymous_sign_ins_enabled = patch.anonymousSignInsEnabled;
      if (patch.autoConfirmEmail !== undefined) row.auto_confirm_email = patch.autoConfirmEmail;

      const { data: userData } = await supabase.auth.getUser();
      if (userData?.user?.id) row.updated_by = userData.user.id;

      const { data, error: updateError } = await supabase
        .from("auth_settings")
        .update(row)
        .eq("id", 1)

        .select("password_hibp_enabled, anonymous_sign_ins_enabled, auto_confirm_email, updated_at, updated_by")
        .maybeSingle();

      setSaving(false);
      if (updateError) {
        const err = new Error(updateError.message);
        setError(err);
        throw err;
      }
      if (data) setSettings(fromRow(data));
    },
    [],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { status, settings, error, saving, refetch, save };
}
